<template>
  <div class="building-owner-form">
    <b-row>
      <h3 v-if="isEditMode">{{ "Modifier le contact" }}</h3>
      <h3 v-else>{{ "Créer un nouveau contact" }}</h3>
    </b-row>
    <!-- gender | lastname | firstname | email -->
    <b-row class="my-3 justify-content-center">
      <b-col cols="12">
        <b-row>
          <b-col cols="6">
            <b-button
              class="btn-block"
              squared
              :class="{
                'agv btn-primary': contact.entity_type == entityTypes.individual,
                'agv btn-outline-primary': contact.entity_type != entityTypes.individual,
              }"
              variant="none"
              @click="setEntityType(entityTypes.individual)"
              ><b>Particulier</b></b-button
            >
          </b-col>
          <b-col cols="6">
            <b-button
              class="btn-block"
              squared
              variant="none"
              :class="{
                'agv btn-primary': contact.entity_type == entityTypes.legal,
                'agv btn-outline-primary': contact.entity_type != entityTypes.legal,
              }"
              @click="setEntityType(entityTypes.legal)"
              ><b>Professionnel</b></b-button
            >
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row class="mb-5 mb-sm-4 mx-sm-0">
      <b-col v-if="contact.entity_type == entityTypes.individual" sm="3" md="2">
        <label>{{ $t("general.Civilité") }}</label>
        <b-form-select
          :value="contact.gender"
          :options="genderOptions"
          @change="updateContact('gender', $event)"
        />
      </b-col>
      <b-col v-if="contact.entity_type == entityTypes.individual" sm="4" md="3">
        <label>{{ $t("general.Nom") }}</label>
        <ValidationProvider
          ref="vee-b-contact-lastname"
          v-slot="{ errors, classes }"
          :name="'nom'"
          rules="required"
        >
          <b-input :value="contact.lastname" @input="updateContact('lastname', $event)" />
          <span :class="classes">{{ errors[0] }}</span>
        </ValidationProvider>
      </b-col>
      <b-col v-if="contact.entity_type == entityTypes.individual" sm="4" md="3">
        <label>{{ $t("general.Prénom") }}</label>
        <b-input :value="contact.firstname" @input="updateContact('firstname', $event)" />
      </b-col>

      <b-col v-if="contact.entity_type == entityTypes.legal" sm="7" md="6">
        <label>Raison sociale</label>
        <ValidationProvider
          ref="vee-b-contact-lastname"
          v-slot="{ errors, classes }"
          :name="'raison sociale'"
          rules="required"
        >
          <b-input :value="contact.lastname" @input="updateContact('lastname', $event)" />
          <span :class="classes">{{ errors[0] }}</span>
        </ValidationProvider>
      </b-col>

      <b-col sm="6" md="4">
        <label>{{ $t("general.Email") }}</label>
        <ValidationProvider
          ref="vee-b-contact-email"
          v-slot="{ errors, classes }"
          :name="'email'"
          :rules="
            isEmailRequired || contact.contact_type == contactTypes.email
              ? 'required|email'
              : 'email'
          "
        >
          <b-input :value="contact.email" @input="updateContact('email', $event)" />
          <span :class="classes">{{ errors[0] }}</span>
        </ValidationProvider>
      </b-col>
    </b-row>
    <!-- street nb | street | address second -->
    <b-row class="mb-5 mb-sm-4 mx-sm-0">
      <b-col sm="6" md="6">
        <label>Adresse</label>
        <b-input :value="contact.address" @input="updateContact('address', $event)" />
      </b-col>
      <b-col sm="6" md="6">
        <label>{{ $t("general.Complément") }}</label>
        <b-input :value="contact.complement" @input="updateContact('complement', $event)" />
      </b-col>
    </b-row>
    <!-- zipcode | city | country -->
    <b-row class="mb-5 mb-sm-4 mx-sm-0">
      <b-col sm="3" md="2">
        <label>{{ $t("general.Code_postal") }}</label>
        <b-input :value="contact.zipcode" @input="updateContact('zipcode', $event)" />
      </b-col>
      <b-col sm="4" md="3">
        <label>Ville</label>
        <b-input :value="contact.city" @input="updateContact('city', $event)" />
      </b-col>
      <b-col sm="4" md="3">
        <label>{{ $t("general.Pays") }}</label>
        <ValidationProvider
          ref="vee-b-contact-country"
          v-slot="{ errors, classes }"
          :name="'pays'"
          rules="required"
        >
          <b-form-select
            :value="contact.country.toUpperCase()"
            :options="countryOptions"
            @input="updateContact('country', $event)"
          >
            <template #first>
              <b-form-select-option :value="''" disabled>Choisissez le pays</b-form-select-option>
            </template>
          </b-form-select>
          <span :class="classes">{{ errors[0] }}</span>
        </ValidationProvider>
      </b-col>

      <b-col sm="5" md="4">
        <label>Complément 2</label>
        <b-input :value="contact.complement2" @input="updateContact('complement2', $event)" />
      </b-col>
    </b-row>
    <!-- contact type -->
    <b-row class="mb-4 mx-sm-0">
      <b-col sm="6" md="5">
        <label>{{ $t("general.Type_d’envoi_préféré") }}</label>
        <ValidationProvider
          ref="vee-b-contact-contact_type"
          v-slot="{ errors, classes }"
          :name="'type d’envoi préféré'"
          rules="required"
        >
          <b-form-select
            :value="contact.contact_type"
            :options="contactTypeOptions"
            @change="updateContact('contact_type', $event)"
          />
          <span :class="classes">{{ errors[0] }}</span>
        </ValidationProvider>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="8">
        <b-form-checkbox
          :value="1"
          :unchecked-value="0"
          :checked="isEmailRequired"
          switch
          class="d-inline text-center ml-4 mt-1"
          @change="sendCredentials"
        />
        <span>
          <font-awesome-icon icon="info-circle" class="info-box-margin-top" />
          {{ "Voulez envoyer des informations d'identification à ce contact ?" }}
        </span>
      </b-col>
    </b-row>
    <b-row class="mt-5 mb-2 justify-content-end">
      <b-col cols="auto">
        <b-button class="button btn-red" @click="cancel">
          <font-awesome-icon class="agv-button-icon" icon="times-circle" />
          <span>{{ $t("general.cancel") }}</span>
        </b-button>
      </b-col>
      <b-col cols="auto">
        <b-button :disabled="isLoadingSave" class="button btn-blue" @click="createOrUpdateContact">
          <font-awesome-icon v-if="!isLoadingSave" class="agv-button-icon" icon="save" />
          <font-awesome-icon v-else class="agv-button-icon" icon="spinner" pulse />
          <span>{{ $t("general.enregistrer") }}</span>
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import ContactMW from "@/middlewares/Contact";
import { validate } from "vee-validate";

export default {
  props: {
    existentContact: {
      type: Object,
      default: () => {
        return {};
      },
    },
    buildingId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      contact: {
        agency_id: Number(localStorage.getItem("agencyId")),
        building_id: this.buildingId,
        address: "",
        zipcode: "",
        city: "",
        country: "FR",
        complement: "",
        complement2: "",
        entity_type: 1,
        contact_type: 1,
        can_vote: 0,
        gender: 1,
        firstname: "",
        lastname: "",
        email: "",
      },
      isLoadingSave: false,
      isEditMode: false,
      isEmailRequired: false,
    };
  },
  computed: {
    countryOptions() {
      let result = [];
      this.allCountries.forEach(element => {
        result.push({
          value: element.countryCode,
          text: element.countryName,
        });
      });
      return result;
    },
    allCountries() {
      return this.$store.getters.getCountries;
    },
  },
  created() {
    this.isCountryValid();
    if (this.existentContact) {
      this.contact = this.existentContact;
      this.isEditMode = true;
    }
  },
  methods: {
    updateContact(key, value) {
      this.contact[key] = value;
    },
    setEntityType(type) {
      this.updateContact("entity_type", type);
      if (type == this.entityTypes.legal) {
        this.$emit("updateContact", "gender", 0);
        this.updateContact("gender", 0);
      }
    },
    isCountryValid() {
      let found = this.$_.find(this.allCountries, country => {
        return country.countryCode.toUpperCase() == this.contact.country.toUpperCase();
      });
      if (found === undefined) {
        this.updateContact("country", "");
      }
    },
    cancel() {
      this.$emit("cancel");
    },
    async validateStep() {
      var valid = true;
      let values = [
        {
          key: "lastname",
          rules: "required",
        },
        {
          key: "email",
          rules:
            this.isEmailRequired || this.contact.contact_type == this.contactTypes.email
              ? "required"
              : "",
        },
      ];

      for (const value of values) {
        await validate(this.contact[value.key], value.rules, {}).then(res => {
          if (!res.valid) {
            valid = false;
            this.$refs["vee-b-contact-" + value.key].validate();
          }
        });
      }
      return valid;
    },
    async createOrUpdateContact() {
      if (!(await this.validateStep())) {
        return;
      }
      this.isLoadingSave = true;
      if (this.existentContact) {
        this.UPDATE_CONTACT();
      } else {
        this.REGISTER_AND_CREATE_CONTACT();
      }
    },
    UPDATE_CONTACT() {
      ContactMW.PUT(this.contact)
        .then(contact => {
          this.contact = contact;
          this.isLoadingSave = false;
          this.cancel();
        })
        .catch(() => {
          this.isLoadingSave = false;
        });
    },
    CREATE_CONTACT() {
      ContactMW.POST(this.contact)
        .then(contact => {
          this.contact = contact;
          this.isLoadingSave = false;
          this.cancel();
        })
        .catch(() => {
          this.isLoadingSave = false;
        });
    },
    REGISTER_AND_CREATE_CONTACT() {
      this.isLoadingSave = true;
      ContactMW.REGISTER(this.contact)
        .then(res => {
          this.contact.user_id = res.userId;
          this.contact.username = res.username;
          this.contact.password = res.password;

          this.CREATE_CONTACT();
        })
        .catch(() => {
          this.isLoadingSave = false;
        });
    },
    sendCredentials() {
      this.isEmailRequired = !this.isEmailRequired;
    },
  },
};
</script>
